import { ProfilePicture } from '@marketing-milk/frontend'
import { FacebookPixel } from '@marketing-milk/interfaces'
import Redux from 'app/interface/redux'
import Connect from 'app/redux/connect'
import { businessManagerService } from 'app/service/business-manager'
import {
  CommonFBBusinessObjectFields,
  FBBusinessAdAccount,
  FBBusinessIGAccount,
  FBBusinessPage,
} from 'app/service/business-manager/BusinessManager.types'
import axios from 'axios'
import Popover from 'components/reusable/popover'
import { groupBy } from 'lodash'
import React, { useEffect, useState } from 'react'
import Select from 'react-select'
import { toast } from 'react-toastify'

export type FBObjectTypeString = 'page' | 'ad_account' | 'pixel' | 'offline' | 'ig'

const formatFBObject = ({ id, name, picture }: CommonFBBusinessObjectFields) => ({
  value: id,
  label: (
    <div className="col-12 d-flex">
      {picture && <ProfilePicture initials={name} image={picture?.data.url} size={50} />}
      <div className="ml-4">
        <h5 className="font-weight-bold">{name}</h5>
        <div>{`id: ${id}`}</div>
      </div>
    </div>
  ),
  searchables: [name],
})

interface FacebookObjectSelectorProps<FBObjectType extends CommonFBBusinessObjectFields>
  extends Redux {
  assignFbObject: (fbObject: FBObjectType | undefined, field: FBObjectTypeString) => void
  title: string
  type: FBObjectTypeString
  disabled: boolean
  accountID?: string
  selectedFbObjectName?: string | null
  selectedFbObjectID?: string
  tooltipText?: string
}

const FacebookObjectSelector = <FBObjectType extends CommonFBBusinessObjectFields>(
  props: FacebookObjectSelectorProps<FBObjectType>
) => {
  const fbConnected = props.core.session.user?.fbConnected
  const [fbObject, setFbObject] = useState<{
    value: string
    label: any
    searchables: string[]
  } | null>(
    props.selectedFbObjectID
      ? formatFBObject({
          id: props.selectedFbObjectID,
          name: props.selectedFbObjectName ?? '-',
          ...(props.type === 'page'
            ? {
                picture: {
                  data: {
                    url: `https://graph.facebook.com/${props.selectedFbObjectID}/picture?type=large`,
                  },
                },
              }
            : {}),
        })
      : null
  )
  const [fbObjects, setFbObjects] = useState<FBObjectType[]>([])
  const [loading, setLoading] = useState<boolean>(false)
  const [hasError, setHasError] = useState<boolean>(false)

  const styles = {
    control: (provided, state) => ({
      ...provided,
      background: '#fff',
      borderColor: hasError ? '#e74c3c' : '#9e9e9e', // Red border on error
      minHeight: '75px',
      height: '75px',
      boxShadow: state.isFocused ? (hasError ? '0 0 0 1px #e74c3c' : '0 0 0 1px #2684FF') : 'none',
      '&:hover': {
        borderColor: hasError ? '#e74c3c' : '#9e9e9e',
      },
    }),
    valueContainer: (provided, state) => ({
      ...provided,
      height: '75px',
      padding: '0 6px',
    }),
    input: (provided, state) => ({
      ...provided,
      margin: '0px',
    }),
    indicatorSeparator: state => ({
      display: 'none',
    }),
    indicatorsContainer: (provided, state) => ({
      ...provided,
      height: '75px',
    }),
  }

  // Resetting internal state if external state changes
  useEffect(() => {
    if (!props.selectedFbObjectID) {
      setFbObject(null)
    }
  }, [props.selectedFbObjectID])

  const getFBObjects = async () => {
    // Non-FB users cannot fetch FB objects
    if (!fbConnected) {
      return
    }

    try {
      setLoading(true)
      const serviceRecordByType: Record<
        FBObjectTypeString,
        (
          accountID?: string
        ) => Promise<
          | FBBusinessPage[]
          | FBBusinessAdAccount[]
          | FBBusinessIGAccount[]
          | Omit<FacebookPixel, 'code'>[]
        >
      > = {
        page: () => businessManagerService.getFBPages(),
        ad_account: () => businessManagerService.getFBAdAccounts(),
        pixel: (accountID?: string) =>
          !!accountID && accountID !== '0'
            ? businessManagerService.getFBDatasets(accountID)
            : Promise.resolve([]),
        offline: (accountID?: string) =>
          !!accountID && accountID !== '0'
            ? businessManagerService.getFBDatasets(accountID)
            : Promise.resolve([]),
        ig: (accountID?: string) =>
          !!accountID && accountID !== '0'
            ? businessManagerService.getFBIGAccounts(accountID)
            : Promise.resolve([]),
      }

      // Only calls the function when awaited
      const response = await serviceRecordByType[props.type](props.accountID)

      // We know what was selected isn't in the user's list of access
      const userLacksAccessToSelectedFBObject =
        props.selectedFbObjectID &&
        !(response as FBObjectType[]).find(({ id }) => id === props.selectedFbObjectID)

      if (userLacksAccessToSelectedFBObject) {
        setHasError(true)
      }

      setFbObjects(response as FBObjectType[])
    } catch (e) {
      if (axios.isAxiosError(e)) {
        toast.error(
          `Failed to load ${props.title.toLowerCase()}s. ${
            e instanceof Error ? e.response?.data.message : String(e)
          }.`
        )
      }

      setFbObjects([])
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    getFBObjects()
  }, [props.accountID])

  useEffect(() => {
    const selected = fbObjects.find(({ id }) => id === props.selectedFbObjectID)

    if (selected) {
      setFbObject(formatFBObject(selected))
      setHasError(false)
    }
  }, [fbObjects])

  const getFBObjectOptions = () => {
    const objectsByBusiness = groupBy(fbObjects, 'business.name')
    const result = Object.entries(objectsByBusiness).map(([businessName, objects]) => ({
      label: businessName !== 'undefined' ? businessName : 'N/A',
      options: objects.map(formatFBObject),
    }))
    return result
  }

  const formatGroupLabel = data => (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
      }}
    >
      <span>{data.label}</span>
    </div>
  )

  const handleFBObjectSelection = selected => {
    if (selected === null) {
      // Handle clearable selects
      setFbObject(null)
      setHasError(false)
      props.assignFbObject(undefined, props.type)
    } else {
      const fbObject = fbObjects?.find(({ id }) => id === selected.value)

      if (fbObject) {
        setFbObject(selected)
        setHasError(false)
        props.assignFbObject(fbObject, props.type)
      }
    }
  }

  return (
    <div className="d-flex flex-column align-items-start px-2 py-2">
      <span className="pb-2">
        {props.title}
        {props.tooltipText && (
          <>
            &nbsp;<Popover>{props.tooltipText}</Popover>
          </>
        )}
      </span>
      <Select
        className="col-12 pl-0"
        options={getFBObjectOptions()}
        onChange={handleFBObjectSelection}
        value={fbObject}
        isLoading={loading}
        isDisabled={props.disabled}
        isClearable={true}
        formatGroupLabel={formatGroupLabel}
        filterOption={(option, input) => {
          const { searchables } = option.data
          for (const search of searchables) {
            if (search.toLowerCase().includes(input.toLowerCase())) {
              return true
            }
          }
          return false
        }}
        styles={styles}
      />
      {hasError && <p className="text-danger">You do not have access to this Facebook asset.</p>}
    </div>
  )
}

export default Connect(FacebookObjectSelector)
